/*
Establishes connections with sportable MQTT brokers and sets up a context to provide these connections to child components.
Returns a MqttContext.Provider with the clients object as the context value and all child components as children.
The clients object contains a key-value pair for each broker that has been connected to.
*/

import { BrokerConfig } from '@frontend/core'
import {
  localBrokerConfig,
  useGetBrokersQuery
} from '@frontend/core/lib/modules/mqtt'
import { selectServerUrl } from '@frontend/core/lib/modules/servers/selectors'
import { extractUrlSegments } from '@frontend/react/utils/url'
import { ReactNode, useEffect, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectLocationName, selectMetricsServerConnected } from '../servers'
import { MQTTContext } from './mqtt.context'
import { useMQTTClient } from './useMQTTClient'

export const MQTTProvider = ({ children }: { children: ReactNode }) => {
  const { client, isConnected, setConfig } = useMQTTClient(null)
  const [localMSConnectionStatus, setLocalMSConnectionStatus] = useState(false)
  const [externalConnections, setExternalConnections] = useState<
    BrokerConfig[]
  >([])

  const metricsServerConnected = useAppSelector(selectMetricsServerConnected)
  const locationName = useAppSelector(selectLocationName)
  const metricsServerUrl = useAppSelector(selectServerUrl('metricsServer'))
  const { ip: metricsServerIp } = extractUrlSegments(metricsServerUrl ?? '')

  // Fetch brokers //
  const { data: brokers, isLoading } = useGetBrokersQuery(null, {
    skip: !metricsServerConnected
  })

  useEffect(() => {
    if (!metricsServerConnected || !brokers) return
    const { port } = extractUrlSegments(localBrokerConfig.WSSHost)

    const broker = {
      ...localBrokerConfig,
      WSSHost: `ws://${metricsServerIp}:${port}`,
      locationName: locationName
    }

    setConfig(broker)

    const newExternalConnections: BrokerConfig[] = []

    // loop through non local brokers to ensure MS is connected to broker
    brokers.forEach((broker) => {
      if (broker.WSSHost === '') return
      if (broker.name?.toLowerCase() === 'local') {
        setLocalMSConnectionStatus(broker.status)
        return
      }

      newExternalConnections.push(broker)
    })

    setExternalConnections(newExternalConnections)
  }, [locationName, metricsServerUrl, metricsServerConnected, brokers])

  return (
    <MQTTContext.Provider
      value={{
        client,
        isConnected,
        externalConnections,
        localMSConnectionStatus
      }}
    >
      {children}
    </MQTTContext.Provider>
  )
}
